import Layout from "../components/layout/Layout";
import SEO, { orgSchema, breadcrumbSchema, webPageSchema, articleSchema } from "../components/layout/SEO";
import Breadcrumbs from "../components/shared/Breadcrumbs";
import { Reveal, StaggerGroup, StaggerItem } from "../components/shared/Reveal";
import { SummaryBox, RelatedLinks } from "../components/shared/SeoBlocks";
import CTAButton from "../components/shared/CTAButton";
import AftercareEnquiryForm from "../components/shared/AftercareEnquiryForm";
import { DOCTOR_REVIEW } from "../content/doctorReviewContent";
import { AFTERCARE } from "../content/aftercareContent";
import { SITE } from "../data/site";
import { useI18n } from "../i18n";
import { Check, ShieldAlert, BadgeCheck, HelpCircle, UserCog } from "lucide-react";

export function DoctorReviewPage() {
  const { lang } = useI18n();
  const c = DOCTOR_REVIEW[lang] || DOCTOR_REVIEW.en;
  return (
    <Layout>
      <SEO title={`${c.metaTitle} | ${SITE.name}`} description={c.metaDescription} path={`/${c.slug}`} type="article"
        schema={[orgSchema(), webPageSchema({ name: c.h1, description: c.metaDescription, path: `/${c.slug}` }),
          breadcrumbSchema([{ name: "Home", to: "/" }, { name: c.h1, to: `/${c.slug}` }])]} />
      <Breadcrumbs items={[{ name: c.h1 }]} />
      <article className="container-psv py-12 md:py-16">
        <Reveal className="max-w-4xl">
          <span className="eyebrow mb-4 block">{c.eyebrow}</span>
          <h1 className="font-serif font-medium text-primary text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight">{c.h1}</h1>
          <p className="mt-6 text-xl text-secondary-foreground font-light leading-relaxed">{c.intro}</p>
        </Reveal>
        <div className="mt-10 max-w-4xl"><SummaryBox>{c.summary}</SummaryBox></div>

        <section className="mt-14 max-w-4xl">
          <Reveal><h2 className="font-serif text-3xl md:text-4xl text-primary mb-3 flex items-center gap-3"><BadgeCheck className="w-7 h-7 text-accent" strokeWidth={1.5} />{c.reviewHeading}</h2>
          <p className="text-[16px] text-secondary-foreground mb-6">{c.reviewIntro}</p></Reveal>
          <StaggerGroup className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
            {c.reviewAreas.map((a) => <StaggerItem key={a} className="flex items-start gap-3 py-2 border-b border-border"><Check className="w-5 h-5 text-accent shrink-0 mt-0.5" strokeWidth={1.5} /><span className="text-[15px] text-secondary-foreground">{a}</span></StaggerItem>)}
          </StaggerGroup>
        </section>

        <section className="mt-14 max-w-4xl">
          <Reveal><h2 className="font-serif text-3xl md:text-4xl text-primary mb-6 flex items-center gap-3"><HelpCircle className="w-7 h-7 text-accent" strokeWidth={1.5} />{c.questionsHeading}</h2></Reveal>
          <ol className="space-y-3">
            {c.questions.map((q, i) => <Reveal as="li" key={i} className="flex items-start gap-3 text-[16px] text-secondary-foreground"><span className="font-serif text-accent text-lg leading-none">{i + 1}.</span>{q}</Reveal>)}
          </ol>
        </section>

        <section className="mt-14 max-w-4xl">
          <Reveal className="bg-surface border-l-2 border-accent rounded-r-lg p-6 md:p-8">
            <h2 className="font-serif text-2xl md:text-3xl text-primary mb-4 flex items-center gap-3"><ShieldAlert className="w-6 h-6 text-accent" strokeWidth={1.5} />{c.limitsHeading}</h2>
            <ul className="space-y-2.5">{c.limits.map((l) => <li key={l} className="flex items-start gap-3 text-[15px] text-secondary-foreground"><span className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />{l}</li>)}</ul>
          </Reveal>
        </section>

        <section className="mt-14 max-w-4xl">
          <Reveal><h2 className="font-serif text-3xl md:text-4xl text-primary mb-3 flex items-center gap-3"><UserCog className="w-7 h-7 text-accent" strokeWidth={1.5} />{c.profileHeading}</h2>
          <p className="text-[16px] text-secondary-foreground mb-6">{c.profileIntro}</p></Reveal>
          <div className="flex flex-wrap gap-2.5">
            {c.profileFields.map((pf) => <span key={pf} className="px-4 py-2 rounded-full border border-border text-sm text-primary/70 bg-surface">{pf}</span>)}
          </div>
          <p className="mt-6 text-[13px] text-muted-foreground italic">{c.profileNote}</p>
        </section>

        <Reveal className="mt-14 max-w-4xl bg-primary text-primary-foreground rounded-lg p-8">
          <p className="text-primary-foreground/85 mb-6 max-w-xl">{c.intro}</p>
          <CTAButton to="/#case-review" variant="accent" testId="doctor-review-cta">{lang === "fr" ? "Demander une étude de dossier" : lang === "de" ? "Fallprüfung anfordern" : "Request a Case Review"}</CTAButton>
        </Reveal>
      </article>
    </Layout>
  );
}

export function AftercarePage() {
  const { lang } = useI18n();
  const c = AFTERCARE[lang] || AFTERCARE.en;
  return (
    <Layout>
      <SEO title={`${c.metaTitle} | ${SITE.name}`} description={c.metaDescription} path={`/${c.slug}`} type="article"
        schema={[orgSchema(), webPageSchema({ name: c.h1, description: c.metaDescription, path: `/${c.slug}` }),
          articleSchema({ title: c.h1, description: c.metaDescription, image: `${SITE.url}/og-image.svg`, path: `/${c.slug}`, datePublished: "2025-12-01", dateModified: "2025-12-01" }),
          breadcrumbSchema([{ name: "Home", to: "/" }, { name: c.h1, to: `/${c.slug}` }])]} />
      <Breadcrumbs items={[{ name: c.eyebrow }]} />
      <article className="container-psv py-12 md:py-16">
        <Reveal className="max-w-4xl">
          <span className="eyebrow mb-4 block">{c.eyebrow}</span>
          <h1 className="font-serif font-medium text-primary text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight">{c.h1}</h1>
          <p className="mt-6 text-xl text-secondary-foreground font-light leading-relaxed">{c.intro}</p>
        </Reveal>

        <Reveal className="mt-8 max-w-4xl bg-primary text-primary-foreground rounded-lg p-6 md:p-7" data-testid="aftercare-disclaimer">
          <p className="text-[15px] leading-relaxed text-primary-foreground/90">{c.disclaimer}</p>
        </Reveal>

        <section className="mt-14 max-w-5xl">
          <Reveal><h2 className="font-serif text-3xl md:text-4xl text-primary mb-6">{c.servicesHeading}</h2></Reveal>
          <StaggerGroup className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3">
            {c.services.map((s) => <StaggerItem key={s} className="flex items-start gap-3 py-2"><Check className="w-5 h-5 text-accent shrink-0 mt-0.5" strokeWidth={1.5} /><span className="text-[15px] text-secondary-foreground">{s}</span></StaggerItem>)}
          </StaggerGroup>
        </section>

        <section className="mt-16">
          <Reveal><h2 className="font-serif text-3xl md:text-4xl text-primary mb-8">{c.packagesHeading}</h2></Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {c.packages.map((pkg, i) => (
              <Reveal key={pkg.name} delay={i * 0.08} className={`flex flex-col rounded-lg border p-7 ${i === 2 ? "border-accent bg-surface" : "border-border bg-background"}`} data-testid={`package-${i}`}>
                <h3 className="font-serif text-2xl text-primary mb-2">{pkg.name}</h3>
                <p className="text-[14px] text-muted-foreground mb-5">{pkg.best}</p>
                <ul className="space-y-2 flex-1">{pkg.inclusions.map((inc) => <li key={inc} className="flex items-start gap-2.5 text-[14px] text-secondary-foreground"><Check className="w-4 h-4 text-accent shrink-0 mt-0.5" strokeWidth={1.5} />{inc}</li>)}</ul>
              </Reveal>
            ))}
          </div>
          <p className="mt-6 text-[13px] text-muted-foreground max-w-4xl" data-testid="packages-disclaimer">{c.packagesDisclaimer}</p>
        </section>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-10 max-w-5xl">
          <section>
            <Reveal><h2 className="font-serif text-2xl md:text-3xl text-primary mb-5">{c.whoHeading}</h2></Reveal>
            <ul className="space-y-2.5">{c.who.map((w) => <li key={w} className="flex items-start gap-3 text-[15px] text-secondary-foreground"><span className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />{w}</li>)}</ul>
          </section>
          <section>
            <Reveal className="bg-surface border-l-2 border-accent rounded-r-lg p-6">
              <h2 className="font-serif text-2xl md:text-3xl text-primary mb-4">{c.nurseHeading}</h2>
              <ul className="space-y-2.5">{c.nurse.map((n) => <li key={n} className="flex items-start gap-3 text-[14px] text-secondary-foreground"><Check className="w-4 h-4 text-accent shrink-0 mt-0.5" strokeWidth={1.5} />{n}</li>)}</ul>
            </Reveal>
          </section>
        </div>

        <section className="mt-14 max-w-4xl">
          <Reveal><h2 className="font-serif text-2xl md:text-3xl text-primary mb-5">{c.afterHeading}</h2></Reveal>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5">{c.after.map((a) => <li key={a} className="flex items-start gap-3 text-[15px] text-secondary-foreground"><Check className="w-5 h-5 text-accent shrink-0 mt-0.5" strokeWidth={1.5} />{a}</li>)}</ul>
        </section>

        <div className="mt-12 flex flex-wrap gap-4">
          <CTAButton href={SITE.whatsappHref} target="_blank" rel="noopener noreferrer" testId="ac-cta-0">{c.ctas[0]}</CTAButton>
          <CTAButton to={`/${c.slug}#aftercare-form`} variant="outline" testId="ac-cta-1">{c.ctas[1]}</CTAButton>
          <CTAButton href={SITE.whatsappHref} target="_blank" rel="noopener noreferrer" variant="ghost" testId="ac-cta-2">{c.ctas[2]}</CTAButton>
        </div>

        <section id="aftercare-form" className="scroll-mt-28 mt-14 max-w-3xl">
          <AftercareEnquiryForm f={c.form} />
        </section>

        <div className="max-w-4xl"><RelatedLinks title={c.relatedHeading} links={c.related} /></div>
      </article>
    </Layout>
  );
}
